import { getRowValueForClientColumnId } from '@/shared/sorm/cross-entity-columns'
import { parseSormBooleanValue } from '@/shared/sorm/format-sorm-semantic-boolean-value'

interface ReadRowValueArgs {
  row: Record<string, unknown>
  columnId: string
  rootEntity: string
}

export function readString(args: ReadRowValueArgs): string | undefined {
  const value = getRowValueForClientColumnId(args)
  return typeof value === 'string' ? value : undefined
}

// SORM serializes numeric/decimal columns (e.g. Lot.display_quantity) as strings.
// Coerce so downstream comparisons work. Whitespace-only strings are treated
// as missing — `Number(' ')` is 0, which would fabricate a value.
export function parseSormNumber(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

export function readNumber(args: ReadRowValueArgs): number | undefined {
  return parseSormNumber(getRowValueForClientColumnId(args))
}

// SORM emits boolean columns in three wire formats, depending on the column's
// semantic_type, the transport, and whether the view casts to Bool: a native
// JSON boolean, a 'true'/'false' string, or a ClickHouse UInt8 1/0.
// parseSormBooleanValue covers the first two; the numeric reader covers the
// third, which would otherwise read as absent and default to the wrong answer.
export function readBoolean(args: ReadRowValueArgs): boolean | undefined {
  const parsed = parseSormBooleanValue(getRowValueForClientColumnId(args))
  if (parsed != null) return parsed

  const numeric = readNumber(args)
  if (numeric === 1) return true
  if (numeric === 0) return false
  return undefined
}
